import { useMediaQuery } from "@mantine/hooks"
import { useAtom, useAtomValue } from "jotai"
import {
  currentOpenPopoverAtom,
  isControlsVisibleAtom,
  isPlayingAtom,
  isPreventedAtom,
} from "../state"
import { useCallback, useEffect, useRef } from "react"

const LARGE_PLAY_BUTTON_SELECTOR = '[data-id="large-play-button"]'

function isLargePlayButtonTarget(target: EventTarget | null) {
  return (
    target instanceof Element && !!target.closest(LARGE_PLAY_BUTTON_SELECTOR)
  )
}

export default function useHideControls() {
  const timeRef = useRef<NodeJS.Timeout | null>(null)
  const [controlsVisible, setControlsVisible] = useAtom(isControlsVisibleAtom)
  const [openPopover] = useAtom(currentOpenPopoverAtom)
  const [isPrevented, setPreventHide] = useAtom(isPreventedAtom)
  const isPlaying = useAtomValue(isPlayingAtom)
  const isTouch = useMediaQuery("(pointer: coarse)")

  useEffect(() => {
    return () => {
      if (timeRef.current) {
        clearTimeout(timeRef.current)
        timeRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isTouch) return
    if (!isPlaying) return
    if (timeRef.current) {
      clearTimeout(timeRef.current)
      timeRef.current = null
    }
    timeRef.current = setTimeout(() => {
      setControlsVisible(false)
    }, 3000)
  }, [isTouch, isPlaying, controlsVisible, setControlsVisible, isPrevented])

  const setTimer = () => {
    if (!isPlaying) return
    if (timeRef.current) {
      clearTimeout(timeRef.current)
      timeRef.current = null
    }
    timeRef.current = setTimeout(() => {
      setControlsVisible(false)
    }, 3000)
  }

  const hideControls = useCallback(() => {
    setControlsVisible(false)
  }, [setControlsVisible])

  return {
    isTouch,
    isVisible: !!openPopover || isPrevented || controlsVisible || !isPlaying,
    hideControls,
    props: isTouch
      ? {
          onTouchEnd: (e) => {
            if (isLargePlayButtonTarget(e.target)) return
            setControlsVisible(true)
            setTimer()
          },
          onTouchMove: (e) => {
            if (isLargePlayButtonTarget(e.target)) return
            setControlsVisible(true)
            setTimer()
          },
        }
      : {
          onMouseEnter: () => setControlsVisible(true),
          onMouseLeave: () => {
            if (!isPlaying) return
            setControlsVisible(false)
          },
          onTouchStart: (e) => {
            setTimer()
            if (
              !controlsVisible &&
              e.target.dataset.id !== "large-play-button"
            ) {
              e.preventDefault?.()
            }
          },
          onTouchEnd: () => {
            if (!isPlaying) {
              setControlsVisible(true)
              return
            }
            setControlsVisible(true)
          },
          onPointerMove: () => {
            setControlsVisible(true)
            if (!isPlaying) return
            setTimer()
          },
        },
    setPreventHide,
  } as const
}
